import { Incident, IncidentDTO } from './model';
import { Icon } from '@piwikpro/ui-components';
import { Severity } from './model';
import React from 'react';

export const HOUR: number = 1000 * 60 * 60;
export const MINUTE: number = 1000 * 60;
export const YEAR: number = 8640000 * 365;

export function formatHour(d: Date): string {
  const h = `0${d.getUTCHours()}`.slice(-2);
  const m = `0${d.getUTCMinutes()}`.slice(-2);
  return `${h}:${m}`;
}

export function formatTime(inc: Incident): string {
  const start = inc.start.toLocaleString();
  const end = inc.end.toLocaleString();
  if (inc.end.getTime() === 0 || isNaN(inc.end.getTime())) {
    return `${start}`;
  } else {
    return `${start} - ${end}`;
  }
}

export function severityIcon(severity: string): string {
  switch (severity) {
    case 'ok':
      return '🟢';
    case 'down':
    case 'downtime':
      return '🔴';
    case 'disrupted':
      return '🟡';
    case 'info':
      return '🔵';
    default:
      console.error('Unknown severity:' + severity);
      return '⚪';
  }
}

export function formatComponents(inc: Incident): string {
  return inc.affected
    .map((inc) => `${inc.name} ${severityIcon(inc.severity)}`)
    .join(' ');
}

export const statusTooltip =
  '🔴: downtime\n\n🟡: disrupted\n\n🔵: info\n\n🟢OK\n\n⚪: unknown';

export function getAffectedComponents(
  incidents: Incident[] | IncidentDTO[]
): string[] {
  return incidents
    .flatMap((inc) => inc.affected)
    .map((c) => c?.name)
    .reduce((acc: string[], v) => {
      if (!v) return acc;
      if (!acc.includes(v)) {
        acc.push(v);
      }
      return acc;
    }, []);
}

export type SeverityMapper = {
  [key in Severity]: string;
};

export type SeverityIconMapper = {
  [key in Severity]: JSX.Element;
};

export const severity2msg: SeverityMapper = {
  ok: 'All Systems Operational',
  info: 'Informational',
  maintenance: 'Maintenance',
  disrupted: 'Performance Degraded',
  down: 'Ongoing Incident',
  unknown: 'Ongoing Incident',
};

export const severity2icon: SeverityIconMapper = {
  down: React.createElement(Icon, {
    name: 'exclamation-mark-circle',
    color: 'red',
  }),
  disrupted: React.createElement(Icon, {
    name: 'exclamation-mark-circle',
    color: 'yellow',
  }),
  ok: React.createElement(Icon, { name: 'checkmark-circle', color: 'green' }),
  maintenance: React.createElement(Icon, {
    name: 'info-circle',
    color: 'blue',
  }),
  info: React.createElement(Icon, {
    name: 'info-circle',
    color: 'blue',
  }),
  unknown: React.createElement(Icon, { name: 'info-circle', color: 'blue' }),
};

export type ComponentMappingEntry = {
  displayName: string;
  description?: string;
  components: string[];
};

export const mapComponentName = (
  name: string,
  mapping: ComponentMappingEntry[]
) => {
  for (const entry of mapping) {
    if (entry.components.includes(name)) {
      return entry.displayName;
    }
  }
  return name;
};
